import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Tab, Tabs, Table } from 'react-bootstrap';
import { FaFingerprint, FaEye, FaMoneyBillWave, FaHistory, FaIdCard, FaShieldAlt, FaCheckCircle, FaUniversity, FaTimes } from 'react-icons/fa';
import api from '../services/api';
import toast from 'react-hot-toast';

const AEPSModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('cash_withdrawal');
  const [customerDetails, setCustomerDetails] = useState({
    aadharNumber: '',
    mobileNumber: '',
    bankCode: ''
  });
  const [transactionDetails, setTransactionDetails] = useState({
    amount: '',
    transactionType: 'CW', // CW=Cash Withdrawal, BE=Balance Enquiry, MS=Mini Statement
    charges: 0
  });
  const [fingerprintStatus, setFingerprintStatus] = useState('pending'); // pending, captured, verified
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [balanceEnquiry, setBalanceEnquiry] = useState(null);
  const [miniStatement, setMiniStatement] = useState([]);
  const [pidData, setPidData] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);  // Actual DeviceInfo XML from RD Service
  const [location, setLocation] = useState({ latitude: '', longitude: '' });  // Store GPS location

  // AEPS supported banks
  const aepsBanks = [
    { code: 'SBIN', name: 'State Bank of India', charges: { cw: 15, be: 5, ms: 5 } },
    { code: 'HDFC', name: 'HDFC Bank', charges: { cw: 12, be: 4, ms: 4 } },
    { code: 'ICICI', name: 'ICICI Bank', charges: { cw: 12, be: 4, ms: 4 } },
    { code: 'AXIS', name: 'Axis Bank', charges: { cw: 10, be: 3, ms: 3 } },
    { code: 'PNB', name: 'Punjab National Bank', charges: { cw: 15, be: 5, ms: 5 } },
    { code: 'BOB', name: 'Bank of Baroda', charges: { cw: 15, be: 5, ms: 5 } },
    { code: 'CANARA', name: 'Canara Bank', charges: { cw: 12, be: 4, ms: 4 } },
    { code: 'UNION', name: 'Union Bank of India', charges: { cw: 12, be: 4, ms: 4 } },
    { code: 'IOB', name: 'Indian Overseas Bank', charges: { cw: 10, be: 3, ms: 3 } },
    { code: 'CENTRAL', name: 'Central Bank of India', charges: { cw: 10, be: 3, ms: 3 } }
  ];

  const serviceTypes = [
    { key: 'cash_withdrawal', label: 'Cash Withdrawal', icon: <FaMoneyBillWave className="text-success" />, code: 'CW' },
    { key: 'balance_enquiry', label: 'Balance Enquiry', icon: <FaEye className="text-info" />, code: 'BE' },
    { key: 'mini_statement', label: 'Mini Statement', icon: <FaHistory className="text-warning" />, code: 'MS' }
  ];

  const mockTransactionHistory = [
    {
      id: 'AEPS001',
      type: 'Cash Withdrawal',
      aadhar: '****8765',
      bank: 'State Bank of India',
      amount: 2000,
      charges: 15,
      status: 'Success',
      date: '2025-01-15',
      time: '14:30:25'
    },
    {
      id: 'AEPS002',
      type: 'Balance Enquiry',
      aadhar: '****8765',
      bank: 'HDFC Bank',
      amount: 0,
      charges: 4,
      status: 'Success',
      date: '2025-01-15',
      time: '10:15:42'
    },
    {
      id: 'AEPS003',
      type: 'Cash Withdrawal',
      aadhar: '****9876',
      bank: 'ICICI Bank',
      amount: 1500,
      charges: 12,
      status: 'Success',
      date: '2025-01-14',
      time: '16:45:12'
    }
  ];

  const mockMiniStatement = [
    { date: '15-Jan-2025', description: 'UPI-9999900001', type: 'Credit', amount: 5000, balance: 25430 },
    { date: '14-Jan-2025', description: 'ATM Withdrawal', type: 'Debit', amount: 2000, balance: 20430 },
    { date: '13-Jan-2025', description: 'Salary Credit', type: 'Credit', amount: 35000, balance: 22430 },
    { date: '12-Jan-2025', description: 'Online Shopping', type: 'Debit', amount: 1500, balance: -12570 },
    { date: '11-Jan-2025', description: 'EMI Debit', type: 'Debit', amount: 8500, balance: -11070 }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage({ type: '', text: '' });
    setFingerprintStatus('pending');
    setBalanceEnquiry(null);
    setMiniStatement([]);
    
    const serviceType = serviceTypes.find(s => s.key === tab);
    if (serviceType) {
      setTransactionDetails(prev => ({
        ...prev,
        transactionType: serviceType.code,
        amount: serviceType.code === 'CW' ? prev.amount : '0'
      }));
    }
    
    if (tab === 'history') {
      setTransactionHistory(mockTransactionHistory);
    }
  };

  const validateAadhar = (aadhar) => {
    return /^\d{12}$/.test(aadhar);
  };

  const validateMobile = (mobile) => {
    return /^[6-9]\d{9}$/.test(mobile);
  };

  const calculateCharges = (type, bankCode, amount = 0) => {
    const bank = aepsBanks.find(b => b.code === bankCode);
    if (!bank) return 0;
    
    switch (type) {
      case 'CW': return bank.charges.cw;
      case 'BE': return bank.charges.be;
      case 'MS': return bank.charges.ms;
      default: return 0;
    }
  };

  const captureFingerprint = async () => {
    if (!validateAadhar(customerDetails.aadharNumber)) {
      setMessage({ type: 'error', text: 'Please enter a valid 12-digit Aadhar number' });
      return;
    }

    if (!customerDetails.bankCode) {
      setMessage({ type: 'error', text: 'Please select a bank' });
      return;
    }

    if (transactionDetails.transactionType === 'CW') {
      const amount = parseFloat(transactionDetails.amount);
      if (!amount || amount < 100 || amount > 10000) {
        setMessage({ type: 'error', text: 'Cash withdrawal amount must be between ₹100 and ₹10,000' });
        return;
      }
    }

    setLoading(true);
    setFingerprintStatus('capturing');
    setMessage({ type: 'info', text: 'Please place your finger on the biometric device...' });

    try {
      const { captureFingerprint: rdCapture, getDeviceInfo, getGpsLocation } = await import('../utils/rdService');

      const gps = await getGpsLocation();
      setLocation(gps);

      // IDEMIA L1 RD Service - get DeviceInfo (required) + capture
      let deviceInfoXml;
      try {
        deviceInfoXml = await getDeviceInfo();
      } catch (e) {
        setFingerprintStatus('failed');
        setMessage({ type: 'error', text: `Device info failed: ${e.message}. Ensure RD Service is running.` });
        console.error('getDeviceInfo failed:', e);
        return;
      }
      const result = await rdCapture();

      if (result.success) {
        const pidDataXml = result.pidData;
        const qualityScore = result.qualityScore;
        if (qualityScore == null || qualityScore >= 50) {
          setPidData(pidDataXml);
          setDeviceInfo(deviceInfoXml);
          setFingerprintStatus('captured');
          setMessage({ type: 'success', text: 'Fingerprint captured successfully.' });
        } else {
          setFingerprintStatus('failed');
          setMessage({ type: 'error', text: `Fingerprint quality too low (${qualityScore}%). Please try again with a clean finger.` });
        }
      } else {
        setFingerprintStatus('failed');
        setMessage({ type: 'error', text: `Fingerprint capture failed (Error ${result.errorCode}: ${result.errorInfo}). Please try again.` });
      }
    } catch (error) {
      setFingerprintStatus('failed');
      const errorMessage = error.message || 'Fingerprint capture failed. Ensure IDEMIA L1 RD Service is running (port 11100).';
      setMessage({ type: 'error', text: errorMessage });
      console.error('Fingerprint capture error:', error);
    } finally {
      setLoading(false);
    }
  };

  const processTransaction = async () => {
    if (fingerprintStatus !== 'captured') {
      setMessage({ type: 'error', text: 'Please capture fingerprint first' });
      return;
    }

    setLoading(true);
    setMessage({ type: 'info', text: 'Processing AEPS transaction...' });

    try {
      const bank = aepsBanks.find(b => b.code === customerDetails.bankCode);
      
      const payload = {
        mobile: customerDetails.mobileNumber,
        aadhar: customerDetails.aadharNumber,
        bank_code: customerDetails.bankCode,
        data: pidData,
        device_info: deviceInfo || '',
        latitude: location.latitude || '28.6139',
        longitude: location.longitude || '77.2090',
      };
      if (transactionDetails.transactionType === 'CW') {
        payload.amount = transactionDetails.amount;
      }

      let endpoint = '';
      let response = null;

      // Call appropriate API based on transaction type
      if (transactionDetails.transactionType === 'CW') {
        // Cash Withdrawal
        payload.amount = transactionDetails.amount;
        endpoint = '/api/aeps/withdrawal';
        response = await api.post(endpoint, payload);
        
      } else if (transactionDetails.transactionType === 'BE') {
        // Balance Enquiry
        endpoint = '/api/aeps/enquiry';
        response = await api.post(endpoint, payload);
        
        // Display balance
        if (response.data.success && response.data.data) {
          setBalanceEnquiry({
            accountNumber: response.data.data.account_number || '****' + customerDetails.aadharNumber.slice(-4),
            availableBalance: response.data.data.balance || response.data.data.availableBalance || 0,
            bank: bank.name,
            timestamp: new Date().toLocaleString()
          });
        }
        
      } else if (transactionDetails.transactionType === 'MS') {
        // Mini Statement
        endpoint = '/api/aeps/ministatement';
        response = await api.post(endpoint, payload);
        
        // Display statement
        if (response.data.success && response.data.data) {
          setMiniStatement(response.data.data.statement || response.data.data.transactions || []);
        }
      }

      // Handle response
      if (response.data.success) {
        const transactionData = {
          transactionId: response.data.transaction_id || response.data.data?.transaction_id || 'AEPS' + Date.now(),
          type: serviceTypes.find(s => s.code === transactionDetails.transactionType)?.label,
          aadharNumber: customerDetails.aadharNumber,
          bank: bank.name,
          amount: parseFloat(transactionDetails.amount || 0),
          charges: transactionDetails.charges,
          status: 'Success',
          timestamp: new Date().toLocaleString()
        };

        setMessage({ 
          type: 'success', 
          text: response.data.message || `AEPS ${transactionData.type} successful!`
        });

        toast.success(`AEPS ${transactionData.type} completed successfully!`);

        if (onSuccess) {
          onSuccess(transactionData);
        }

        // Don't auto-close for BE and MS to show results
        if (transactionDetails.transactionType === 'CW') {
          setTimeout(() => {
            onHide();
          }, 3000);
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: response.data.message || 'Transaction failed. Please try again.' 
        });
      }

    } catch (error) {
      console.error('AEPS Transaction Error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Transaction failed. Please try again.' 
      });
      toast.error('AEPS transaction failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Success': 'success',
      'Failed': 'danger',
      'Pending': 'warning'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getFingerprintIcon = () => {
    switch (fingerprintStatus) {
      case 'captured':
        return <FaCheckCircle className="text-success" size={50} />;
      case 'failed':
        return <FaTimes className="text-danger" size={50} />;
      default:
        return <FaFingerprint className="text-primary" size={50} />;
    }
  };

  const handleReset = () => {
    setCustomerDetails({ aadharNumber: '', mobileNumber: '', bankCode: '' });
    setTransactionDetails({ amount: '', transactionType: 'CW', charges: 0 });
    setFingerprintStatus('pending');
    setBalanceEnquiry(null);
    setMiniStatement([]);
    setMessage({ type: '', text: '' });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-success text-white">
        <Modal.Title>
          <FaFingerprint className="me-2" />
          AEPS - Aadhaar Enabled Payment System
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* AEPS Service Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          {serviceTypes.map((service) => (
            <Tab 
              key={service.key}
              eventKey={service.key} 
              title={
                <span>
                  {service.icon}
                  <span className="ms-2">{service.label}</span>
                </span>
              }
            >
              <div className="p-2">
                <h6 className="text-success mb-3">{service.label}</h6>
              </div>
            </Tab>
          ))}
          <Tab eventKey="history" title={
            <span>
              <FaHistory className="me-2" />
              Transaction History
            </span>
          }>
            <div className="p-2">
              <h6 className="text-success mb-3">AEPS Transaction History</h6>
            </div>
          </Tab>
        </Tabs>

        {/* Transaction History */}
        {activeTab === 'history' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6>Recent AEPS Transactions</h6>
              <Badge bg="success">Total: ₹3,500</Badge>
            </div>
            
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Type</th>
                  <th>Aadhar</th>
                  <th>Bank</th>
                  <th>Amount</th>
                  <th>Charges</th>
                  <th>Status</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {transactionHistory.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.id}</td>
                    <td>{transaction.type}</td>
                    <td>{transaction.aadhar}</td>
                    <td>{transaction.bank}</td>
                    <td>₹{transaction.amount.toLocaleString()}</td>
                    <td>₹{transaction.charges}</td>
                    <td>{getStatusBadge(transaction.status)}</td>
                    <td>
                      <small>{transaction.date}<br/>{transaction.time}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* AEPS Transaction Forms */}
        {activeTab !== 'history' && (
          <Row>
            {/* Customer Details */}
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header>
                  <FaIdCard className="me-2" />
                  Customer Details
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Aadhar Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="12-digit Aadhar number"
                      value={customerDetails.aadharNumber}
                      onChange={(e) => setCustomerDetails({
                        ...customerDetails, 
                        aadharNumber: e.target.value.replace(/\D/g, '').slice(0, 12)
                      })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Registered mobile number"
                      value={customerDetails.mobileNumber}
                      onChange={(e) => setCustomerDetails({
                        ...customerDetails, 
                        mobileNumber: e.target.value.replace(/\D/g, '').slice(0, 10)
                      })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Select Bank *</Form.Label>
                    <Form.Select
                      value={customerDetails.bankCode}
                      onChange={(e) => {
                        const bankCode = e.target.value;
                        const charges = calculateCharges(transactionDetails.transactionType, bankCode, transactionDetails.amount);
                        setCustomerDetails({...customerDetails, bankCode});
                        setTransactionDetails(prev => ({...prev, charges}));
                      }}
                    >
                      <option value="">Choose bank...</option>
                      {aepsBanks.map((bank) => (
                        <option key={bank.code} value={bank.code}>
                          {bank.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {activeTab === 'cash_withdrawal' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Withdrawal Amount (₹) *</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter amount (₹100 - ₹10,000)"
                        value={transactionDetails.amount}
                        onChange={(e) => {
                          const amount = e.target.value;
                          const charges = calculateCharges(transactionDetails.transactionType, customerDetails.bankCode, amount);
                          setTransactionDetails({
                            ...transactionDetails, 
                            amount: amount,
                            charges: charges
                          });
                        }}
                        min="100"
                        max="10000"
                      />
                    </Form.Group>
                  )}

                  {transactionDetails.charges > 0 && (
                    <Alert variant="info">
                      <strong>Service Charges:</strong> ₹{transactionDetails.charges}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Biometric Authentication */}
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header>
                  <FaFingerprint className="me-2" />
                  Biometric Authentication
                </Card.Header>
                <Card.Body className="text-center">
                  <div className="mb-4">
                    {getFingerprintIcon()}
                  </div>
                  
                  <div className="mb-3">
                    <Badge bg={
                      fingerprintStatus === 'captured' ? 'success' :
                      fingerprintStatus === 'failed' ? 'danger' :
                      fingerprintStatus === 'capturing' ? 'warning' : 'secondary'
                    }>
                      {fingerprintStatus === 'captured' ? 'Captured' :
                       fingerprintStatus === 'failed' ? 'Failed' :
                       fingerprintStatus === 'capturing' ? 'Capturing' : 'Pending'}
                    </Badge>
                  </div>

                  {fingerprintStatus === 'pending' && (
                    <Button 
                      variant="success" 
                      onClick={captureFingerprint}
                      disabled={loading}
                      className="mb-3"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Capturing...
                        </>
                      ) : (
                        <>
                          <FaFingerprint className="me-2" />
                          Capture Fingerprint
                        </>
                      )}
                    </Button>
                  )}

                  {fingerprintStatus === 'captured' && (
                    <Button 
                      variant="primary" 
                      onClick={processTransaction}
                      disabled={loading}
                      className="mb-3"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="me-2" />
                          Process Transaction
                        </>
                      )}
                    </Button>
                  )}

                  <div className="mt-3">
                    <small className="text-muted">
                      Biometric authentication is secured by UIDAI encryption
                    </small>
                  </div>
                </Card.Body>
              </Card>

              {/* Transaction Results */}
              {balanceEnquiry && (
                <Card className="mb-4">
                  <Card.Header className="bg-info text-white">
                    <FaEye className="me-2" />
                    Balance Enquiry Result
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2"><strong>Account:</strong> {balanceEnquiry.accountNumber}</div>
                    <div className="mb-2"><strong>Bank:</strong> {balanceEnquiry.bank}</div>
                    <div className="mb-2"><strong>Available Balance:</strong> ₹{balanceEnquiry.availableBalance.toLocaleString()}</div>
                    <div className="mb-2"><strong>Enquiry Time:</strong> {balanceEnquiry.timestamp}</div>
                  </Card.Body>
                </Card>
              )}

              {miniStatement.length > 0 && (
                <Card>
                  <Card.Header className="bg-warning text-dark">
                    <FaHistory className="me-2" />
                    Mini Statement
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm" striped>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {miniStatement.map((entry, index) => (
                          <tr key={index}>
                            <td><small>{entry.date}</small></td>
                            <td><small>{entry.description}</small></td>
                            <td>
                              <Badge bg={entry.type === 'Credit' ? 'success' : 'danger'} size="sm">
                                {entry.type}
                              </Badge>
                            </td>
                            <td><small>₹{entry.amount.toLocaleString()}</small></td>
                            <td><small>₹{entry.balance.toLocaleString()}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Message */}
        {message.text && (
          <Alert variant={
            message.type === 'error' ? 'danger' : 
            message.type === 'info' ? 'info' : 'success'
          } className="mt-3">
            {message.text}
          </Alert>
        )}

        {/* Security Notice */}
        <Alert variant="success" className="mb-0">
          <FaShieldAlt className="me-2" />
          <strong>Secure AEPS Transaction:</strong> All biometric data is encrypted and processed through UIDAI-certified systems. Your fingerprint is never stored.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {activeTab !== 'history' && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AEPSModal;
